import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import useApi from "../../hooks/useApi";

const normalizeGeneros = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return value ? [value] : [];
  }
};

export default function DirectLink() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { loadApi } = useApi();
  const [difusor, setDifusor] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let activo = true;
    setLoading(true);
    setError("");

    loadApi(`general/direct-links/${encodeURIComponent(slug)}`, false, "get")
      .then(({ data }) => {
        if (activo) setDifusor(data);
      })
      .catch((e) => {
        if (activo) setError(e.message || "Este Direct Link no está disponible.");
      })
      .finally(() => {
        if (activo) setLoading(false);
      });

    return () => {
      activo = false;
    };
  }, [slug]);

  const generos = useMemo(
    () => normalizeGeneros(difusor?.generos_musicales),
    [difusor?.generos_musicales]
  );

  const iniciales = useMemo(() => {
    const nombre = String(difusor?.nombre_media || "Difusor").trim();
    return nombre
      .split(/\s+/)
      .slice(0, 2)
      .map((parte) => parte[0])
      .join("")
      .toUpperCase();
  }, [difusor?.nombre_media]);

  const iniciar = (service) => {
    navigate(
      `/d/${encodeURIComponent(slug)}/${encodeURIComponent(service.id)}`
    );
  };

  if (loading) {
    return (
      <Stack minHeight="100vh" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (error || !difusor) {
    return (
      <Container maxWidth="sm" sx={{ py: 10 }}>
        <Alert severity="info">
          {error || "Este Direct Link no está disponible."}
        </Alert>
      </Container>
    );
  }

  return (
    <Box minHeight="100vh" bgcolor="#f4f4f6" pb={8}>
      <Box
        minHeight={{ xs: 180, sm: 260 }}
        sx={{
          background: difusor.bannerimg
            ? `center/cover no-repeat url(${difusor.bannerimg})`
            : "linear-gradient(135deg, #171717 0%, #3a2115 55%, #F27224 100%)",
        }}
      />
      <Container maxWidth="md" sx={{ mt: { xs: -7, sm: -9 } }}>
        <Card sx={{ borderRadius: 4, overflow: "visible" }}>
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            <Stack alignItems="center" textAlign="center" spacing={2}>
              <Avatar
                src={difusor.profileimg || undefined}
                alt={difusor.nombre_media || "Difusor"}
                sx={{
                  width: { xs: 104, sm: 128 },
                  height: { xs: 104, sm: 128 },
                  border: "5px solid white",
                  mt: -10,
                  bgcolor: difusor.profileimg ? "transparent" : "#202020",
                  color: "white",
                  fontWeight: 900,
                  fontSize: "2rem",
                  boxShadow: "0 10px 28px rgba(0,0,0,0.18)",
                }}
              >
                {!difusor.profileimg && iniciales}
              </Avatar>

              <Typography
                fontWeight={900}
                sx={{
                  fontSize: { xs: "2rem", sm: "2.35rem", md: "2.55rem" },
                  lineHeight: 1.05,
                  letterSpacing: "-0.03em",
                  whiteSpace: { xs: "normal", md: "nowrap" },
                }}
              >
                Solicita difusión con {difusor.nombre_media}
              </Typography>

              {difusor.descripcion && (
                <Typography color="text.secondary" maxWidth={680}>
                  {difusor.descripcion}
                </Typography>
              )}

              <Box
                sx={{
                  width: "100%",
                  maxWidth: 720,
                  border: "1px solid rgba(242,114,36,0.28)",
                  bgcolor: "rgba(242,114,36,0.055)",
                  borderRadius: 3,
                  px: { xs: 2, sm: 3 },
                  py: 2,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.25}
                  alignItems="center"
                  justifyContent="center"
                >
                  <VerifiedUserOutlinedIcon color="primary" />
                  <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                    Estás en el Direct Link verificado de{" "}
                    <strong>{difusor.nombre_media}</strong>. Cada servicio cuesta{" "}
                    <strong>
                      {difusor.price?.radarCoin || 5} RadarCoins · USD $
                      {difusor.price?.usd || "12.50"}
                    </strong>
                    . RadarPay protege el pago y mantiene tu solicitud registrada y
                    rastreable dentro de RadarFreim.
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={1}
                >
                  El medio se compromete a atender la solicitud dentro del plazo
                  indicado. La aprobación final depende de su criterio editorial; si
                  el pedido vence sin respuesta, tus RadarCoins regresan a tu cuenta.
                </Typography>
              </Box>

              {generos.length > 0 && (
                <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="center">
                  {generos.map((genero) => (
                    <Chip key={genero} label={genero} size="small" />
                  ))}
                </Stack>
              )}
            </Stack>

            <Stack mt={5} spacing={2}>
              <Typography variant="h5" fontWeight={800}>
                ¿Qué quieres solicitar?
              </Typography>
              {(!difusor.services || difusor.services.length === 0) && (
                <Alert severity="info">
                  Este difusor todavía no ha configurado servicios en su Direct Link.
                </Alert>
              )}
              {difusor.services?.map((service) => (
                <Card key={service.id} variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={800}>
                          {service.nombre}
                        </Typography>
                        <Typography color="text.secondary">
                          {service.descripcion}
                        </Typography>
                      </Box>
                      <Stack alignItems={{ xs: "stretch", sm: "flex-end" }} gap={1}>
                        <Typography fontWeight={900} color="primary.main">
                          {service.radarCoin} RadarCoins · USD ${service.usd}
                        </Typography>
                        <Button onClick={() => iniciar(service)}>
                          Solicitar este servicio
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>

            <Typography mt={4} variant="body2" color="text.secondary" textAlign="center">
              No necesitas registrarte para comenzar. Primero envías tu solicitud y,
              después del pago, activas tu cuenta gratuita de RadarFreim para darle seguimiento.
            </Typography>

            <Box
              mt={3}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                pt: 3,
                textAlign: "center",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "primary.main",
                    color: "white",
                  }}
                >
                  <LockOutlinedIcon fontSize="small" />
                </Box>
                <Typography
                  component="div"
                  sx={{
                    fontWeight: 900,
                    fontSize: "1.35rem",
                    letterSpacing: "-0.03em",
                  }}
                >
                  Radar
                  <Box component="span" sx={{ color: "primary.main" }}>
                    Pay
                  </Box>
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={700} mt={1}>
                Operación protegida y monitoreada por RadarPay.
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                maxWidth={620}
                mx="auto"
                mt={0.5}
              >
                Tu pago queda vinculado a una solicitud dentro de RadarFreim y puedes
                seguir su estado desde tu cuenta. Si el medio no la atiende dentro del
                plazo, el sistema devuelve automáticamente tus RadarCoins.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
